import { Suspense } from 'react';

import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
} from 'react-router-dom';

import { AdminOnlyRoute } from '@/components/auth/AdminOnlyRoute';
import { AppShell } from '@/components/layout/AppShell';
import { useAuth } from '@/hooks/useAuth';
import { CAMERAS_PATH, isCameraViewerPath, useCameraAccess } from '@/hooks/useCameraAccess';
import {
  isProductionPayrollPath,
  useProductionPayrollAccess,
} from '@/hooks/useProductionPayrollAccess';
import { hasSellerWork, useSellerWorkspaceAccess } from '@/hooks/useSellerWorkspaceAccess';
import { cn } from '@/lib/cn';
import { lazyPage } from '@/lib/lazy-page';
import { appCompatRoutes, rootCompatRoutes } from '@/pages/compat/legacyRoutes';
import RouteErrorBoundaryPage from '@/pages/RouteErrorBoundaryPage';
import {
  isStockContentScrollRoute,
  SELLER_WORKSPACE_PATH,
  stockRoute,
} from '@/pages/stock/stockRoutes';
import { ProductAnalyticsRouteObserver } from '@/telemetry/ProductAnalyticsRouteObserver';

/** Payroll: administrators and granted users (MD-0097); others go to their home page. */
function ProductionPayrollRoute() {
  const { canUse, isLoading } = useProductionPayrollAccess();
  if (isLoading) return <PageLoadingFallback fullScreen={false} />;
  if (!canUse) return <Navigate to={SELLER_WORKSPACE_PATH} replace />;
  return <Outlet />;
}

/**
 * Camera overview and store pages: administrators and granted users (MD-0086 addendum
 * 2026-09-28); others go to their home page. The zone editor stays administrator-only.
 */
function CameraViewerRoute() {
  const { canView, isLoading } = useCameraAccess();
  if (isLoading) return <PageLoadingFallback fullScreen={false} />;
  if (!canView) return <Navigate to={SELLER_WORKSPACE_PATH} replace />;
  return <Outlet />;
}

function isSellerWorkspacePath(pathname: string): boolean {
  return pathname === SELLER_WORKSPACE_PATH || pathname.startsWith(`${SELLER_WORKSPACE_PATH}/`);
}

// Lazy-loaded page components
const LandingPage = lazyPage(() => import('@/pages/LandingPage'));
const PrivacyPage = lazyPage(() => import('@/pages/PrivacyPage'));
const TermsPage = lazyPage(() => import('@/pages/TermsPage'));
const LoginPage = lazyPage(() => import('@/pages/LoginPage'));
const OwnerReportPage = lazyPage(() => import('@/pages/owner-report/OwnerReportPage'));
const TvDashboardPage = lazyPage(() => import('@/pages/tv/TvDashboardPage'));
const TvCamerasPage = lazyPage(() => import('@/pages/tv/TvCamerasPage'));
const TvDevicesPage = lazyPage(() => import('@/pages/settings/TvDevicesPage'));
const InvoicesPage = lazyPage(() => import('@/pages/InvoicesPage'));
const InvoiceDetailPage = lazyPage(() => import('@/pages/invoices/InvoiceDetailPage'));
const PaymentsPage = lazyPage(() => import('@/pages/PaymentsPage'));
const BankPage = lazyPage(() => import('@/pages/BankPage'));
const BankReceiptsPage = lazyPage(() => import('@/pages/finance/BankReceiptsPage'));
const MoneyInboxPage = lazyPage(() => import('@/pages/finance/MoneyInboxPage'));
const CardTerminalsPage = lazyPage(() => import('@/pages/finance/CardTerminalsPage'));
const AnalyticsDashboardPage = lazyPage(() => import('@/pages/analytics/AnalyticsDashboardPage'));
const AnalyticsSalesPage = lazyPage(() => import('@/pages/analytics/AnalyticsSalesPage'));
const AnalyticsStockPage = lazyPage(() => import('@/pages/analytics/AnalyticsStockPage'));
const AnalyticsExpensesPage = lazyPage(() => import('@/pages/analytics/AnalyticsExpensesPage'));
const AnalyticsPnlPage = lazyPage(() => import('@/pages/analytics/AnalyticsPnlPage'));
const AnalyticsRawPurchasesPage = lazyPage(() => import('@/pages/analytics/AnalyticsRawPurchasesPage'));
const AnalyticsPlatformsPage = lazyPage(() => import('@/pages/analytics/AnalyticsPlatformsPage'));
const AnalyticsActivityPage = lazyPage(() => import('@/pages/analytics/AnalyticsActivityPage'));
const AnalyticsLayout = lazyPage(() => import('@/pages/analytics/AnalyticsLayout'));
const DeliveryReconciliationPage = lazyPage(
  () => import('@/pages/reconciliation/DeliveryReconciliationPage'),
);
const ReconciliationCasesPage = lazyPage(
  () => import('@/pages/reconciliation/ReconciliationCasesPage'),
);
const ReferencesPage = lazyPage(() => import('@/pages/ReferencesPage'));
const UsersPage = lazyPage(() => import('@/pages/UsersPage'));
const GoposOrdersPage = lazyPage(() => import('@/pages/gopos/OrdersPage'));
// GoposCatalogPage is now redirected to stock/products
const GoposIntegrationPage = lazyPage(() => import('@/pages/settings/GoposIntegrationPage'));
const GeminiHealthPage = lazyPage(() => import('@/pages/settings/GeminiHealthPage'));
const KsefIntegrationPage = lazyPage(() => import('@/pages/settings/KsefIntegrationPage'));
const PosSettingsPage = lazyPage(() => import('@/pages/settings/PosSettingsPage'));
const PosContinuityPage = lazyPage(() => import('@/pages/settings/PosContinuityPage'));
const PosDiscountsPage = lazyPage(() => import('@/pages/discounts/PosDiscountsPage'));

const StockOperationsPage = lazyPage(() => import('@/pages/stock/OperationsPage'));
const LaborRatesPage = lazyPage(() => import('@/pages/settings/LaborRatesPage'));
const AgentAccessPage = lazyPage(() => import('@/pages/settings/AgentAccessPage'));
const SettingsLayout = lazyPage(() => import('@/pages/settings/SettingsLayout'));

const CamerasPage = lazyPage(() => import('@/pages/cameras/CamerasPage'));
const CameraZonesPage = lazyPage(() => import('@/pages/cameras/zones/ZoneEditorPage'));
const ProductionPayrollPage = lazyPage(() => import('@/pages/production/ProductionPayrollPage'));
const SellerPayrollPage = lazyPage(() => import('@/pages/sellers/SellerPayrollPage'));

// Stores — Store Hub
const StoresLayout = lazyPage(() => import('@/pages/stores/StoresLayout'));
const StoresListPage = lazyPage(() => import('@/pages/stores/StoresListPage'));
const StoreOverviewPage = lazyPage(() => import('@/pages/stores/StoreOverviewPage'));
const StoreProductsPage = lazyPage(() => import('@/pages/stores/StoreProductsPage'));
const StoreLabelsPage = lazyPage(() => import('@/pages/stores/StoreLabelsPage'));
const StoreCashPage = lazyPage(() => import('@/pages/stores/StoreCashPage'));
const CashExceptionsPage = lazyPage(() => import('@/pages/stores/CashExceptionsPage'));
const CashPositionPage = lazyPage(() => import('@/pages/stores/CashPositionPage'));
const ShiftsPage = lazyPage(() => import('@/pages/stores/ShiftsPage'));
const RegistersPage = lazyPage(() => import('@/pages/stores/RegistersPage'));
const StoreShiftsPage = lazyPage(() => import('@/pages/stores/StoreShiftsPage'));
const StoreOperationsPage = lazyPage(() => import('@/pages/stores/StoreOperationsPage'));
const StoreSalesPage = lazyPage(() => import('@/pages/stores/StoreSalesPage'));
const StoreEmployeesPage = lazyPage(() => import('@/pages/stores/StoreEmployeesPage'));
const StoreSettingsPage = lazyPage(() => import('@/pages/stores/StoreSettingsPage'));

// Stores — Magazyn (warehouse sub-layout)
const MagazynLayout = lazyPage(() => import('@/pages/stores/magazyn/MagazynLayout'));
const MagazynBalancesPage = lazyPage(() => import('@/pages/stores/magazyn/MagazynBalancesPage'));
const MagazynTransfersPage = lazyPage(() => import('@/pages/stores/magazyn/MagazynTransfersPage'));
const MagazynInventoryPage = lazyPage(() => import('@/pages/stores/magazyn/MagazynInventoryPage'));
const MagazynInventoryCountPage = lazyPage(
  () => import('@/pages/stores/magazyn/MagazynInventoryCountPage'),
);

type PageLoadingFallbackProps = {
  /** When true, fill the viewport; otherwise fill the parent container. */
  fullScreen?: boolean;
};

const CONTENT_SCROLL_EXACT_ROUTES = new Set([
  '/webadmin/app/invoices',
  '/webadmin/app/payments',
  '/webadmin/app/stores',
  '/webadmin/app/cameras',
]);

const CONTENT_SCROLL_ROUTE_PREFIXES = [
  '/webadmin/app/invoices/',
  '/webadmin/app/settings/',
  '/webadmin/app/stores/',
  '/webadmin/app/cameras/',
  '/webadmin/app/gopos/orders',
] as const;

/**
 * Loading fallback for lazy-loaded pages.
 * Uses skeleton structure per ui-skills guidelines.
 */
function PageLoadingFallback({ fullScreen = true }: PageLoadingFallbackProps) {
  return (
    <div
      className={cn(
        'flex items-center justify-center',
        'bg-background text-foreground',
        fullScreen ? 'h-dvh' : 'min-h-full',
      )}
    >
      <div className="flex flex-col items-center gap-4">
        {/* Skeleton loader */}
        <div className="size-8 animate-spin rounded-full border-2 border-muted border-t-primary" />
        <p className="text-pretty text-sm text-muted-foreground">Загрузка...</p>
      </div>
    </div>
  );
}

/**
 * Auth loading state while checking authentication.
 */
function AuthLoadingFallback({ status }: { status: 'idle' | 'loading' | 'restoring' }) {
  const message = status === 'restoring' ? 'Восстанавливаем сессию...' : 'Проверка авторизации...';
  return (
    <div className={cn('flex h-dvh items-center justify-center', 'bg-background text-foreground')}>
      <div className="flex flex-col items-center gap-4">
        <div className="size-8 animate-spin rounded-full border-2 border-muted border-t-primary" />
        <p className="text-pretty text-sm text-muted-foreground">{message}</p>
      </div>
    </div>
  );
}

/**
 * Terminal auth bootstrap error: session restore gave up because the server
 * is unreachable. Shown instead of the spinner; retry restarts the probe.
 */
export function AuthUnreachableFallback({ onRetry }: { onRetry: () => void }) {
  return (
    <div className={cn('flex h-dvh items-center justify-center', 'bg-background text-foreground')}>
      <div className="flex max-w-sm flex-col items-center gap-4 px-6 text-center">
        <p className="text-pretty text-base font-medium">Сервер недоступен</p>
        <p className="text-pretty text-sm text-muted-foreground">
          Не удалось восстановить сессию: сервер не отвечает. Проверьте подключение и повторите
          попытку.
        </p>
        <button
          type="button"
          onClick={onRetry}
          className={cn(
            'inline-flex h-9 items-center justify-center rounded-md px-4 text-sm font-medium',
            'bg-primary text-primary-foreground hover:bg-primary/90',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          )}
        >
          Повторить
        </button>
      </div>
    </div>
  );
}

export function getAppShellScrollMode(pathname: string): 'page' | 'content' {
  const normalizedPath = pathname.replace(/\/$/, '');
  if (
    isStockContentScrollRoute(normalizedPath) ||
    CONTENT_SCROLL_EXACT_ROUTES.has(normalizedPath)
  ) {
    return 'content';
  }
  if (CONTENT_SCROLL_ROUTE_PREFIXES.some((prefix) => normalizedPath.startsWith(prefix))) {
    return 'content';
  }
  return 'page';
}

export function getAppShellContentClassName(pathname: string): string | undefined {
  return pathname.replace(/\/$/, '') === '/webadmin/app/analytics/pnl' ? 'pt-0 lg:pt-0' : undefined;
}

/**
 * Protected route wrapper.
 * Redirects to login if not authenticated.
 */
function ProtectedRoute() {
  const { status, isAuthenticated, user, checkAuth } = useAuth();
  const { contextQuery, isSellerWorkspaceUser, profile } = useSellerWorkspaceAccess();
  const payrollAccess = useProductionPayrollAccess();
  const cameraAccess = useCameraAccess();
  const location = useLocation();

  const scrollMode = getAppShellScrollMode(location.pathname);
  const appShellClassName = getAppShellContentClassName(location.pathname);

  // Show loading while checking auth
  if (status === 'idle' || status === 'loading' || status === 'restoring') {
    return <AuthLoadingFallback status={status} />;
  }

  if (status === 'unreachable') {
    return <AuthUnreachableFallback onRetry={() => void checkAuth()} />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    const from = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to="/webadmin/app/login" replace state={{ from }} />;
  }

  if (user?.role !== 'admin' && contextQuery.isLoading) {
    return <AuthLoadingFallback status="loading" />;
  }

  // A non-admin with no seller work but a camera grant (a technician) is at home on the
  // cameras page, not in an empty seller workspace.
  const sellerWork = hasSellerWork(profile);
  if (isSellerWorkspaceUser && !sellerWork && cameraAccess.isLoading) {
    return <AuthLoadingFallback status="loading" />;
  }
  const home =
    isSellerWorkspaceUser && !sellerWork && cameraAccess.canView
      ? CAMERAS_PATH
      : SELLER_WORKSPACE_PATH;
  if (home === CAMERAS_PATH && isSellerWorkspacePath(location.pathname)) {
    return <Navigate to={CAMERAS_PATH} replace />;
  }

  if (isSellerWorkspaceUser && !isSellerWorkspacePath(location.pathname)) {
    // Pages a per-user grant opens beyond the seller workspace (MD-0097, MD-0086).
    const granted = isProductionPayrollPath(location.pathname)
      ? payrollAccess
      : isCameraViewerPath(location.pathname)
        ? { canUse: cameraAccess.canView, isLoading: cameraAccess.isLoading }
        : null;
    if (granted?.isLoading) return <AuthLoadingFallback status="loading" />;
    if (!granted?.canUse) return <Navigate to={home} replace />;
  }

  return (
    <AppShell
      scrollMode={scrollMode}
      {...(appShellClassName ? { className: appShellClassName } : {})}
    >
      <ProductAnalyticsRouteObserver />
      <Suspense fallback={<PageLoadingFallback fullScreen={false} />}>
        <Outlet />
      </Suspense>
    </AppShell>
  );
}

/**
 * Public route wrapper.
 * Redirects to main page if already authenticated.
 */
function PublicRoute() {
  const { status, isAuthenticated, user, checkAuth } = useAuth();
  const { contextQuery, isSellerWorkspaceUser } = useSellerWorkspaceAccess();

  // Show loading while checking auth
  if (status === 'idle' || status === 'loading' || status === 'restoring') {
    return <AuthLoadingFallback status={status} />;
  }

  if (status === 'unreachable') {
    return <AuthUnreachableFallback onRetry={() => void checkAuth()} />;
  }

  // Redirect to invoices if already authenticated
  if (isAuthenticated) {
    if (user?.role !== 'admin' && contextQuery.isLoading) {
      return <AuthLoadingFallback status="loading" />;
    }
    return (
      <Navigate
        to={isSellerWorkspaceUser ? SELLER_WORKSPACE_PATH : '/webadmin/app/invoices'}
        replace
      />
    );
  }

  return (
    <Suspense fallback={<PageLoadingFallback />}>
      <Outlet />
    </Suspense>
  );
}

/**
 * Application router configuration.
 * Base path: /webadmin/app/ (legacy /web-admin/app/* redirects here)
 */
export const router = createBrowserRouter(
  [
    ...rootCompatRoutes,
    {
      path: '/',
      element: (
        <Suspense fallback={<PageLoadingFallback />}>
          <LandingPage />
        </Suspense>
      ),
    },
    {
      path: '/privacy',
      element: (
        <Suspense fallback={<PageLoadingFallback />}>
          <PrivacyPage />
        </Suspense>
      ),
    },
    {
      path: '/terms',
      element: (
        <Suspense fallback={<PageLoadingFallback />}>
          <TermsPage />
        </Suspense>
      ),
    },
    {
      path: '/webadmin/app',
      errorElement: <RouteErrorBoundaryPage />,
      children: [
        {
          index: true,
          element: <Navigate to="/webadmin/app/invoices" replace />,
        },
        {
          // Owners' report (MD-0128): a Telegram Mini App with its own sign-in, outside the
          // web-admin session; an admin in a browser reads it with the ordinary session.
          path: 'owner',
          element: <OwnerReportPage />,
        },
        {
          // The home TV (owner 18.10.2026): its own long-lived key, outside the web-admin
          // session and the app shell; today's revenue and the live camera wall only.
          path: 'tv',
          element: <TvDashboardPage />,
        },
        {
          path: 'tv/cameras',
          element: <TvCamerasPage />,
        },
        {
          path: 'login',
          element: <PublicRoute />,
          children: [
            {
              index: true,
              element: <LoginPage />,
            },
          ],
        },
        {
          element: <ProtectedRoute />,
          children: [
            {
              path: 'invoices',
              element: <InvoicesPage />,
            },
            {
              path: 'invoices/:id',
              element: <InvoiceDetailPage />,
            },
            {
              path: 'payments',
              element: <PaymentsPage />,
            },
            {
              path: 'bank',
              element: <BankPage />,
            },
            // MD-0092: money ledger and reconciliation cases are admin-only.
            {
              element: <AdminOnlyRoute />,
              children: [
                { path: 'bank/receipts', element: <BankReceiptsPage /> },
                { path: 'money/inbox', element: <MoneyInboxPage /> },
                { path: 'money/cards', element: <CardTerminalsPage /> },
                { path: 'reconciliation/cases', element: <ReconciliationCasesPage /> },
              ],
            },
            {
              element: <AnalyticsLayout />,
              children: [
                {
                  path: 'analytics',
                  element: <AnalyticsDashboardPage />,
                },
                {
                  path: 'analytics/sales',
                  element: <AnalyticsSalesPage />,
                },
                {
                  path: 'analytics/stock',
                  element: <AnalyticsStockPage />,
                },
                {
                  path: 'analytics/expenses',
                  element: <AnalyticsExpensesPage />,
                },
                {
                  path: 'analytics/pnl',
                  element: <AnalyticsPnlPage />,
                },
                {
                  path: 'analytics/raw-purchases',
                  element: <AnalyticsRawPurchasesPage />,
                },
                {
                  path: 'analytics/platforms',
                  element: <AnalyticsPlatformsPage />,
                },
                // MD-0099: team activity is visible to administrators only.
                {
                  element: <AdminOnlyRoute />,
                  children: [{ path: 'analytics/activity', element: <AnalyticsActivityPage /> }],
                },
                {
                  path: 'gopos/orders',
                  element: <GoposOrdersPage />,
                },
                {
                  path: 'stock/operations',
                  element: <StockOperationsPage />,
                },
                {
                  path: 'stock/movements',
                  element: <Navigate to="/webadmin/app/stock/operations" replace />,
                },
              ],
            },
            {
              path: 'reconciliation',
              element: <DeliveryReconciliationPage />,
            },
            {
              path: 'references',
              element: <ReferencesPage />,
            },
            {
              path: 'gopos/catalog',
              element: <Navigate to="/webadmin/app/stock/products" replace />,
            },
            ...appCompatRoutes,
            // MD-0086: store cameras for administrators and users with a camera grant
            // (live video and recordings); the zone editor stays administrator-only.
            {
              element: <CameraViewerRoute />,
              children: [
                { path: 'cameras', element: <CamerasPage /> },
                { path: 'cameras/:cameraStoreId', element: <CamerasPage /> },
              ],
            },
            {
              element: <AdminOnlyRoute />,
              children: [{ path: 'cameras/:cameraStoreId/zones', element: <CameraZonesPage /> }],
            },
            // Production payroll: administrators and users with a payroll grant (MD-0097).
            {
              element: <ProductionPayrollRoute />,
              children: [{ path: 'production/payroll', element: <ProductionPayrollPage /> }],
            },
            // Seller payroll (hourly store sellers): administrators only (owner 2026-10-02).
            {
              element: <AdminOnlyRoute />,
              children: [{ path: 'sellers/payroll', element: <SellerPayrollPage /> }],
            },
            // Stores — Store Hub
            {
              element: <StoresLayout />,
              children: [
                { path: 'stores', element: <StoresListPage /> },
                { path: 'stores/cash-exceptions', element: <CashExceptionsPage /> },
                { path: 'stores/cash-position', element: <CashPositionPage /> },
                { path: 'stores/shifts', element: <ShiftsPage /> },
                { path: 'stores/registers', element: <RegistersPage /> },
                { path: 'stores/:storeId', element: <StoreOverviewPage /> },
                { path: 'stores/:storeId/products', element: <StoreProductsPage /> },
                { path: 'stores/:storeId/labels', element: <StoreLabelsPage /> },
                { path: 'stores/:storeId/cash', element: <StoreCashPage /> },
                { path: 'stores/:storeId/shifts', element: <StoreShiftsPage /> },
                { path: 'stores/:storeId/operations', element: <StoreOperationsPage /> },
                { path: 'stores/:storeId/sales', element: <StoreSalesPage /> },
                { path: 'stores/:storeId/employees', element: <StoreEmployeesPage /> },
                {
                  path: 'stores/:storeId/inventory',
                  element: <Navigate to="../magazyn" replace />,
                },
                { path: 'stores/:storeId/settings', element: <StoreSettingsPage /> },
                {
                  path: 'stores/:storeId/magazyn',
                  element: <MagazynLayout />,
                  children: [
                    { index: true, element: <MagazynBalancesPage /> },
                    { path: 'transfers', element: <MagazynTransfersPage /> },
                    { path: 'inventory', element: <MagazynInventoryPage /> },
                    {
                      path: 'inventory/:inventoryId',
                      element: <MagazynInventoryCountPage />,
                    },
                  ],
                },
              ],
            },
            {
              path: 'discounts',
              element: <PosDiscountsPage />,
            },
            // Settings — unified layout with secondary sidebar
            {
              path: 'settings',
              element: <SettingsLayout />,
              children: [
                { index: true, element: <Navigate to="labor-rates" replace /> },
                { path: 'labor-rates', element: <LaborRatesPage /> },
                { path: 'pos', element: <PosSettingsPage /> },
                { path: 'pos-continuity', element: <PosContinuityPage /> },
                { path: 'gopos', element: <GoposIntegrationPage /> },
                { path: 'ksef', element: <KsefIntegrationPage /> },
                { path: 'gemini-ocr', element: <GeminiHealthPage /> },
                {
                  path: 'agent-access',
                  element: <AdminOnlyRoute />,
                  children: [{ index: true, element: <AgentAccessPage /> }],
                },
                {
                  path: 'users',
                  element: <AdminOnlyRoute />,
                  children: [{ index: true, element: <UsersPage /> }],
                },
                {
                  path: 'tv',
                  element: <AdminOnlyRoute />,
                  children: [{ index: true, element: <TvDevicesPage /> }],
                },
              ],
            },
            stockRoute,
          ],
        },
        {
          path: '*',
          element: <Navigate to="/webadmin/app" replace />,
        },
      ],
    },
    {
      path: '*',
      element: <Navigate to="/webadmin/app" replace />,
    },
  ],
  {
    basename: '/',
    future: {
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  },
);

/**
 * App Router component - wraps RouterProvider.
 */
export function AppRouter() {
  return <RouterProvider router={router} future={{ v7_startTransition: true }} />;
}
